import { FC, useMemo } from 'react';
import Box from '@amzn/awsui-components-core/box';
import Link from '@amzn/awsui-components-core/link';
import Table, { TableProps } from '@amzn/awsui-components-core/table';
import { CampaignASIN } from 'src/@types/valueMatching/common';
import { useTranslate } from 'src/hooks/useTranslate';

const CONFLICT_TABLE_BUNDLE = 'molecules.conflictTable';

const COLUMN_IDS = {
    CAMPAIGN_ID: 'campaignId',
    CAMPAIGN_TITLE: 'campaignTitle',
    ASIN: 'asin',
} as const;

const TRANSLATION_KEYS = {
    COLUMN_CAMPAIGN_ID: 'column_campaign_id',
    COLUMN_CAMPAIGN_TITLE: 'column_campaign_title',
    COLUMN_ASIN: 'column_asin',
    EMPTY_MESSAGE: 'empty_message',
} as const;

export interface ConflictTableProps {
    /** Array of campaign-ASIN conflict records to display */
    conflicts: CampaignASIN[];
    /** Callback invoked when a campaign ID link is clicked */
    onCampaignClick?: (campaignId: string) => void;
}

const ConflictTable: FC<ConflictTableProps> = ({ conflicts, onCampaignClick }) => {
    const { translate } = useTranslate(CONFLICT_TABLE_BUNDLE);

    const columnDefinitions = useMemo<TableProps.ColumnDefinition<CampaignASIN>[]>(
        () => [
            {
                id: COLUMN_IDS.CAMPAIGN_ID,
                header: translate(TRANSLATION_KEYS.COLUMN_CAMPAIGN_ID),
                cell: (item) =>
                    onCampaignClick ? (
                        <Link onFollow={() => onCampaignClick(item.campaignId)}>{item.campaignId}</Link>
                    ) : (
                        item.campaignId
                    ),
            },
            {
                id: COLUMN_IDS.CAMPAIGN_TITLE,
                header: translate(TRANSLATION_KEYS.COLUMN_CAMPAIGN_TITLE),
                cell: (item) => item.campaignTitle || '-',
            },
            {
                id: COLUMN_IDS.ASIN,
                header: translate(TRANSLATION_KEYS.COLUMN_ASIN),
                cell: (item) => item.asin,
            },
        ],
        [translate, onCampaignClick],
    );

    return (
        <Table
            columnDefinitions={columnDefinitions}
            items={conflicts}
            variant="embedded"
            empty={
                <Box textAlign="center" color="inherit">
                    {translate(TRANSLATION_KEYS.EMPTY_MESSAGE)}
                </Box>
            }
        />
    );
};

export default ConflictTable;
